const SDKSnippets = [
  {
    language: 'jsx',
    codeString: (
      publicKey = 'YOUR_PUBLIC_KEY',
      _privateKey = 'YOUR_PRIVATE_KEY',
      urlEndpoint = 'YOUR_ENDPOINT',
    ) =>
      `// Install: npm install @imagekit/react

import { Image, ImageKitProvider } from '@imagekit/react';

export default function Page() {
  return (
    <ImageKitProvider urlEndpoint="${urlEndpoint}">
      <Image
        src="/default-image.jpg"
        width={400}
        height={300}
        alt="Image"
      />
    </ImageKitProvider>
  )
}`,
    tabLabel: 'React',
    docsUrl: 'https://docs.imagekit.io/integration/react',
  },
  {
    language: 'jsx',
    codeString: (
      publicKey = 'YOUR_PUBLIC_KEY',
      _privateKey = 'YOUR_PRIVATE_KEY',
      urlEndpoint = 'YOUR_ENDPOINT',
    ) =>
      `// Install: npm install @imagekit/angular

import { IKImageDirective, provideImageKit } from '@imagekit/angular';

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [IKImageDirective],
  template: \`
    <img
      ikSrc="/default-image.jpg"
      [width]="400"
      [height]="300"
      alt="Image"
    />
  \`
})
export class AppComponent {}

// Configure globally in app.config.ts:
export const appConfig: ApplicationConfig = {
  providers: [
    provideImageKit({
      urlEndpoint: "${urlEndpoint}",
    })
  ]
};`,
    tabLabel: 'Angular',
    docsUrl: 'https://docs.imagekit.io/integration/angular',
  },
  {
    language: 'jsx',
    codeString: (
      publicKey = 'YOUR_PUBLIC_KEY',
      _privateKey = 'YOUR_PRIVATE_KEY',
      urlEndpoint = 'YOUR_ENDPOINT',
    ) =>
      `<!-- Install: npm i @imagekit/vue -->

<script setup lang="ts">
import { ImageKitProvider, Image } from '@imagekit/vue'
</script>

<template>
  <ImageKitProvider url-endpoint="${urlEndpoint}">
    <Image
      src="/default-image.jpg"
      width="400"
      height="300"
      alt="Image" />
  </ImageKitProvider>
</template>`,
    tabLabel: 'Vue.js',
    docsUrl: 'https://docs.imagekit.io/integration/vuejs',
  },
  {
    language: 'kotlin',
    codeString: (
      publicKey = 'YOUR_PUBLIC_KEY',
      _privateKey = 'YOUR_PRIVATE_KEY',
      urlEndpoint = 'YOUR_ENDPOINT',
    ) => `import com.imagekit.android.ImageKit

ImageKit.init(
    context = applicationContext,
    publicKey = "${publicKey}",
    urlEndpoint = "${urlEndpoint}",
    transformationPosition = TransformationPosition.PATH,
    defaultUploadPolicy = UploadPolicy.Builder()
        .requireNetworkType(UploadPolicy.NetworkType.ANY)
        .setMaxRetries(3)
        .build()
)

// URL generation
val imageUrl = ImageKit.getInstance()
    .url(
        path = "default-image.jpg",
        transformationPosition = TransformationPosition.QUERY
    )
    .height(400f)
    .width(300f)
    .create()`,
    tabLabel: 'Android (kotlin)',
    docsUrl: 'https://docs.imagekit.io/integration/android',
  },
  {
    language: 'java',
    codeString: (
      publicKey = 'YOUR_PUBLIC_KEY',
      _privateKey = 'YOUR_PRIVATE_KEY',
      urlEndpoint = 'YOUR_ENDPOINT',
    ) => `import com.imagekit.android.ImageKit;

ImageKit.Companion.init(
    getApplicationContext(),
    "${publicKey}",
    "${urlEndpoint}",
    TransformationPosition.PATH,
    UploadPolicy.Builder()
        .requireNetworkType(UploadPolicy.NetworkType.ANY)
        .setMaxRetries(3)
        .build()
);

// URL generation
String imageUrl = ImageKit.Companion.getInstance()
    .url(
        "default-image.jpg",
        TransformationPosition.QUERY
    )
    .height(400f)
    .width(300f)
    .create();`,
    tabLabel: 'Android (java)',
    docsUrl: 'https://docs.imagekit.io/integration/android',
  },
  {
    language: 'javascript',
    codeString: (
      publicKey = 'YOUR_PUBLIC_KEY',
      _privateKey = 'YOUR_PRIVATE_KEY',
      urlEndpoint = 'YOUR_ENDPOINT',
    ) => `// Install: npm install @imagekit/javascript

import { buildSrc, upload } from '@imagekit/javascript';

// URL generation
const imageURL = buildSrc({
    urlEndpoint: "${urlEndpoint}",
    src: "/default-image.jpg",
    transformation: [{
        height: 300,
        width: 400
    }]
});

// File upload (requires authentication parameters from your server)
const result = await upload({
    file: fileInput.files[0],
    fileName: "my-file.jpg",
    publicKey: "${publicKey}",
    signature: "signature_from_server",
    token: "token_from_server",
    expire: expiryTimestamp // A Unix timestamp in seconds, less than 1 hour in the future. Example: 1616161616
});`,
    tabLabel: 'Javascript',
    docsUrl: 'https://docs.imagekit.io/integration/javascript',
  },
  {
    language: 'python',
    codeString: (
      publicKey = 'YOUR_PUBLIC_KEY',
      privateKey = 'YOUR_PRIVATE_KEY',
      urlEndpoint = 'YOUR_ENDPOINT',
    ) => `# Install: pip install imagekitio

from imagekitio import ImageKit

imagekit = ImageKit(
    private_key='${privateKey}'
)`,
    tabLabel: 'Python',    docsUrl: 'https://docs.imagekit.io/integration/python',  },
  {
    language: 'javascript',
    codeString: (
      publicKey = 'YOUR_PUBLIC_KEY',
      privateKey = 'YOUR_PRIVATE_KEY',
      urlEndpoint = 'YOUR_ENDPOINT',
    ) => `// Install: npm install @imagekit/nodejs

import ImageKit from '@imagekit/nodejs';

const client = new ImageKit({
    privateKey: "${privateKey}"
});`,
    tabLabel: 'Node.js',
    docsUrl: 'https://docs.imagekit.io/integration/nodejs',
  },
  {
    language: 'ruby',
    codeString: (
      publicKey = 'YOUR_PUBLIC_KEY',
      privateKey = 'YOUR_PRIVATE_KEY',
      urlEndpoint = 'YOUR_ENDPOINT',
    ) => `# Install: gem install imagekitio

require 'imagekitio'

client = Imagekitio::Client.new(
  private_key: '${privateKey}'
)`,
    tabLabel: 'Ruby',
    docsUrl: 'https://docs.imagekit.io/integration/ruby',
  },
  {
    language: 'java',
    codeString: (
      publicKey = 'YOUR_PUBLIC_KEY',
      privateKey = 'YOUR_PRIVATE_KEY',
      urlEndpoint = 'YOUR_ENDPOINT',
    ) => `// Add dependency: com.github.imagekit-developer:imagekit-java:2.0.0

import io.imagekit.sdk.ImageKit;
import io.imagekit.sdk.config.Configuration;

public class App {
    public static void main(String[] args) {
        ImageKit imageKit = ImageKit.getInstance();
        Configuration config = new Configuration(
            "${publicKey}",
            "${privateKey}",
            "${urlEndpoint}"
        );
        imageKit.setConfig(config);
    }
}`,
    tabLabel: 'Java',
    docsUrl: 'https://docs.imagekit.io/integration/java',
  },
  {
    language: 'php',
    codeString: (
      publicKey = 'YOUR_PUBLIC_KEY',
      privateKey = 'YOUR_PRIVATE_KEY',
      urlEndpoint = 'YOUR_ENDPOINT',
    ) => `// Install: composer require imagekit/imagekit

require_once __DIR__ . '/vendor/autoload.php';

use ImageKit\\ImageKit;

$imageKit = new ImageKit(
    "${publicKey}",
    "${privateKey}",
    "${urlEndpoint}"
);`,
    tabLabel: 'PHP',
    docsUrl: 'https://docs.imagekit.io/integration/php',
  },
  {
    language: 'csharp',
    codeString: (
      publicKey = 'YOUR_PUBLIC_KEY',
      privateKey = 'YOUR_PRIVATE_KEY',
      urlEndpoint = 'YOUR_ENDPOINT',
    ) => `// Install: dotnet add package Imagekit

using Imagekit;

var imagekit = new ImageKit({
    publicKey : "${publicKey}",
    privateKey : "${privateKey}",
    urlEndpoint : "${urlEndpoint}"
});`,
    tabLabel: '.NET',
    docsUrl: 'https://docs.imagekit.io/integration/dotnet',
  },
  {
    language: 'go',
    codeString: (
      publicKey = 'YOUR_PUBLIC_KEY',
      privateKey = 'YOUR_PRIVATE_KEY',
      urlEndpoint = 'YOUR_ENDPOINT',
    ) => `// Install: go get github.com/imagekit-developer/imagekit-go/v2

import (
    "github.com/imagekit-developer/imagekit-go/v2"
    "github.com/imagekit-developer/imagekit-go/v2/option"
)

client := imagekit.NewClient(
    option.WithPrivateKey("${privateKey}"),
)`,
    tabLabel: 'Go',
    docsUrl: 'https://docs.imagekit.io/integration/go',
  },
]

export default SDKSnippets
